import React, { CSSProperties, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import {
  IMAGE_ASSET_SIZE_MIDDLE,
  getBigAssetPath,
  getMediumAssetPath,
  getSmallAssetPath
} from '@aytm/lib/common/assets'
import { Image as PrimeImage } from 'primereact/image'
import { Tooltip } from 'primereact/tooltip'
import { layoutIsCompact } from '../../lib/layout'

import { IImageAssetProps } from './types'

import useSession from '../../session/useSession'
import useViewport from '../viewport/useViewport'
import useSurveyLocale from '../locale/useSurveyLocale'

import commonStyle from './style.module.scss'

import protectorImageSrc from './protector/image.jpg'
import DevLog from '../devlog/Module'

const tooltipStyleVars = {
  '--prime-tooltip-border-radius': '8px',
  '--prime-tooltip-padding-v': '3px',
  '--prime-tooltip-padding-h': '3px',
  '--prime-tooltip-bg': 'var(--prime-accent-a-color)',
  '--prime-tooltip-arrow-color': 'var(--prime-accent-a-color)'
} as React.CSSProperties

const SMALL_MOBILE_ASSET_SIZE = 30
const BIG_MOBILE_ASSET_SIZE = 36

const getThumbnailPosition = (
  width: number,
  height: number,
  trimmedWidth: number,
  trimmedHeight: number
): CSSProperties =>
  trimmedWidth / width >= trimmedHeight / height
    ? {
        width: '100%'
      }
    : {
        height: height || IMAGE_ASSET_SIZE_MIDDLE
      }

const unFocus = () => {
  if (document.activeElement) {
    try {
      ;(document.activeElement as HTMLElement).blur?.()
    } catch {
      // ignore
    }
  }
}

const ImageAsset: FunctionComponent<IImageAssetProps> = ({
  asset,
  width,
  height,
  preview = true,
  noPreloader,
  className,
  background,
  style,
  onFullImageLoaded,
  openPreview,
  size,
  noTooltip,
  fill,
  position,
  ...props
}) => {
  const { t } = useSurveyLocale()
  const session = useSession()
  const viewport = useViewport()

  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(true)
  const [bigImageLoaded, setBigImageLoaded] = useState<boolean>(false)
  const [bigImageSource, setBigImageSource] = useState<string>(null)
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)

  const tooltipRef = useRef<Tooltip>()
  const imageRef = useRef<HTMLDivElement>()
  const previewImageRef = useRef<PrimeImage>()
  const mainImageRef = useRef<PrimeImage>()

  const protectorSrc = session.features.imageProtector ? protectorImageSrc : void 0,
    imageWidth = viewport.mobile ? (viewport.width > 360 ? BIG_MOBILE_ASSET_SIZE : SMALL_MOBILE_ASSET_SIZE) : width,
    imageHeight = viewport.mobile ? (viewport.width > 360 ? BIG_MOBILE_ASSET_SIZE : SMALL_MOBILE_ASSET_SIZE) : height,
    compactLayout = viewport.mobile || layoutIsCompact(session.props.layout) || viewport.touch,
    showTooltip = bigImageLoaded && Boolean(asset) && !noTooltip && asset?.original_url && !compactLayout,
    preloader = Boolean('small' != size)

  useEffect(() => {
    if (isLoaded || !asset) return
    const timer = setTimeout(() => {
      DevLog.error(new Error('ImageAssetLoadTimeout'), asset.original_url)
    }, 5000)
    return () => clearTimeout(timer)
  }, [isLoaded, asset])

  useEffect(() => {
    if (!preloader && !viewport.mobile) {
      const img = new Image()
      const bigSrc = 'big' == size ? getBigAssetPath(asset) : getMediumAssetPath(asset)
      img.addEventListener('load', () => {
        setBigImageLoaded(true)
      })
      img.src = bigSrc
      setBigImageSource(bigSrc)
    }
  }, [])

  const smallAssetPath = useMemo(() => {
    return getSmallAssetPath(asset, fill)
  }, [asset, fill])

  const handleImageOnError = () => {
    DevLog.log('ImageAssetAfterLoadError', asset?.original_url)
  }

  const handleImageOnLoaded = () => {
    if (openPreview && previewImageRef && previewImageRef.current) previewImageRef.current.show()
    onFullImageLoaded?.()
    setIsLoaded(true)

    if (asset) {
      const img = new Image()
      img.addEventListener('load', () => {
        setError(false)
      })
      img.addEventListener('error', error => {
        DevLog.log('ImageAsset', error, asset.original_url)
        fetch(asset.original_url)
          .then(response => {
            DevLog.log('ImageAssetAfterFetchSuccess', response, asset.original_url)
          })
          .catch(error => {
            DevLog.log('ImageAssetAfterFetchError', error, asset.original_url)
          })
      })
      img.src = asset.original_url
    }
  }

  const showPreviewImage = () => {
    setTimeout(() => {
      setTooltipVisible(false)
    }, 100)
  }

  const hidePreviewImage = () => {
    setTooltipVisible(false)
  }

  const onTooltipImageLoad = () => {
    tooltipRef.current?.realign()
  }

  const onMouseEnter = () => {
    setTooltipVisible(true)
  }

  const onFocus = () => {
    if (imageRef.current && tooltipRef.current) {
      setTooltipVisible(true)
      tooltipRef.current.show({
        currentTarget: imageRef.current,
        target: imageRef.current,
        originalEvent: {} as React.SyntheticEvent
      } as any)
    }
  }

  const onBlur = () => {
    setTimeout(() => {
      setTooltipVisible(false)
      tooltipRef.current?.hide()
    }, 100)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if ([' ', 'Enter'].includes(event.key)) {
      event.preventDefault()
      mainImageRef?.current?.show?.()
    }
  }

  const accessibilityAttributes = asset
    ? ({
        tabIndex: 0,
        'aria-haspopup': 'dialog',
        'aria-label': `${asset.alt_text || t('questions', 'img', 'noAlt')}. ${
          viewport.mobile ? t('questions', 'img', 'altHintMobile') : t('questions', 'img', 'altHintDesktop')
        }`
      } as const)
    : {}

  return (
    <>
      {preloader ? (
        <div className={commonStyle.wrapper} data-testid="SW-Image">
          {asset?.thumbnail16x16 && asset?.trimmed_image && !isLoaded && !noPreloader && (
            <img
              className={commonStyle.thumbnail}
              style={{
                maxWidth: asset.trimmed_image?.width || 'unset',
                maxHeight: asset.trimmed_image?.height || 'unset',
                visibility: isLoaded ? 'hidden' : 'visible',
                ...getThumbnailPosition(width, height || width, asset?.trimmed_image.width, asset?.trimmed_image.height)
              }}
              src={asset.thumbnail16x16}
              role="presentation"
            />
          )}
          <PrimeImage
            {...props}
            alt={asset?.alt_text || t('questions', 'img', 'noAlt')}
            ref={previewImageRef}
            className={`
              ${commonStyle.asset}
              ${className ? className : ''}
              ${compactLayout ? commonStyle.mobile : ''}
            `}
            previewSrc={bigImageSource}
            protectorSrc={protectorSrc}
            preview={preview && Boolean(asset)}
            previewAppendTo={session.document?.body}
            previewOverlayClassName={`
              ${session.themeClassNames}
              ${compactLayout ? commonStyle.previewMobile : ''}
              ${commonStyle.preview}
              ${asset?.scrolling ? commonStyle.scrollingPreview : ''}
            `}
            template={renderPreloader()}
            src={asset?.original_url}
            style={{
              ...style,
              display: isLoaded ? 'block' : 'none'
            }}
            onLoad={handleImageOnLoaded}
            onError={handleImageOnError}
            onShow={showPreviewImage}
            usePreviewRotate={false}
            previewIndicatorClassName={`${
              !asset ? commonStyle.hiddenIndicator : error ? commonStyle.noIndicator : commonStyle.previewIndicator
            }`}
          />
        </div>
      ) : (
        <div
          className={`
            ${className}
            ${commonStyle.imageWrapper}
            ${viewport.mobile && isLoaded && !background && asset ? commonStyle.mobileImage : ''}
          `}
          style={{ width: imageWidth, height: imageHeight }}
          data-testid="SW-Image"
          ref={imageRef}
        >
          {!isLoaded && asset && !noPreloader && <div className={commonStyle.preloader} />}
          {asset ? (
            <PrimeImage
              {...props}
              {...accessibilityAttributes}
              alt={asset.alt_text || t('questions', 'img', 'noAlt')}
              src={noTooltip || compactLayout ? asset?.original_url : smallAssetPath}
              onLoad={handleImageOnLoaded}
              onShow={unFocus}
              onError={handleImageOnError}
              onMouseEnter={onMouseEnter}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              previewSrc={noTooltip ? bigImageSource : smallAssetPath}
              protectorSrc={protectorSrc}
              preview={preview && Boolean(asset) && (noTooltip || compactLayout)}
              previewAppendTo={session.document?.body}
              previewOverlayClassName={`
                ${session.themeClassNames}
                ${commonStyle.preview}
                ${compactLayout ? commonStyle.previewMobile : ''}
                ${asset?.scrolling ? commonStyle.scrollingPreview : ''}
              `}
              imageStyle={{
                opacity: isLoaded ? 1 : 0,
                cursor: bigImageLoaded && !noTooltip ? 'pointer' : 'default'
              }}
              template={renderPreloader()}
              previewIndicatorClassName={`${
                !asset || (compactLayout && !asset?.expand) ? commonStyle.hiddenIndicator : commonStyle.previewIndicator
              }`}
              usePreviewRotate={false}
            />
          ) : (
            ''
          )}
          {showTooltip && (
            <Tooltip
              ref={tooltipRef}
              target={imageRef.current}
              className={`
                ${session.themeClassNames}
                ${commonStyle.tooltip}
                ${error || !tooltipVisible ? commonStyle.invisible : ''} 
              `}
              style={tooltipStyleVars}
              appendTo={session.document?.body}
              position={position || 'left'}
              autoHide={false}
              hideDelay={100}
              data-testid="SW-Image-Preview"
            >
              <PrimeImage
                {...props}
                alt={asset.alt_text || t('questions', 'img', 'noAlt')}
                ref={mainImageRef}
                src={asset?.original_url}
                previewSrc={bigImageSource}
                protectorSrc={protectorSrc}
                preview={preview}
                previewAppendTo={session.document?.body}
                previewOverlayClassName={`${session.themeClassNames} ${commonStyle.preview} ${
                  asset?.scrolling ? commonStyle.scrollingPreview : ''
                }`}
                onShow={showPreviewImage}
                onHide={hidePreviewImage}
                onLoad={onTooltipImageLoad}
                template={renderPreloader()}
                usePreviewRotate={false}
                onMouseEnter={onMouseEnter}
              />
            </Tooltip>
          )}
        </div>
      )}
    </>
  )
}

export default ImageAsset

function renderPreloader() {
  return <i className={`icon icon-aytmfd-zoom-in ${commonStyle.icon} `}></i>
}
